import { baseApi } from "./baseApi";

export interface RequestApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface CreateRequestPayload {
  name: string;
  phone: string;
  email?: string;
  city?: string;
  projectType?: string;
  message: string;
}

export interface UpdateRequestPayload extends CreateRequestPayload {
  id: string;
  status?: string;
}

export interface RequestRecord {
  id: string;
  name: string;
  phone: string;
  email: string;
  city?: string;
  projectType: string;
  message: string;
  createdAt: string;
  status: string;
}

const normalizePhoneNumber = (phone?: string) => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (!digits) {
    return null;
  }

  if (digits.length === 10) {
    return digits;
  }

  if (digits.length === 12 && digits.startsWith("91")) {
    return digits.slice(2);
  }

  return String(phone ?? "").trim() || null;
};

const normalizeStatus = (status: unknown, statusCode?: number) => {
  const normalized = String(status ?? "").trim().toLowerCase();

  if (normalized) {
    return normalized;
  }

  if (statusCode === 0) {
    return "pending";
  }

  return "unknown";
};

const normalizeRequestRecord = (item: Record<string, any>): RequestRecord => ({
  id: String(item.id ?? item.requestId ?? item.requestID ?? 0),
  name: item.name ?? item.clientName ?? item.fullName ?? "",
  phone: item.phone ?? item.mobile ?? item.phoneNumber ?? "",
  email: item.email ?? "",
  city: item.city ?? item.location ?? "",
  projectType: item.projectType ?? item.category ?? item.type ?? "General",
  message: item.message ?? item.requirement ?? item.description ?? "",
  createdAt: item.createdDate ?? item.createdAt ?? item.date ?? "",
  status: normalizeStatus(item.status, item.statusCode),
});

const getRequestItems = (response: RequestApiResponse<unknown> | unknown) => {
  const data = (response as RequestApiResponse<unknown>)?.data ?? response;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray((data as Record<string, unknown> | undefined)?.items)) {
    return (data as { items: Record<string, any>[] }).items;
  }

  return [] as Record<string, any>[];
};

export const requestsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createRequest: builder.mutation<RequestApiResponse, CreateRequestPayload>({
      query: (payload) => ({
        url: "/CreateRequest",
        method: "POST",
        body: {
          name: payload.name ?? "",
          email: payload.email ?? "",
          phone: normalizePhoneNumber(payload.phone),
          city: payload.city ?? "",
          projectType: payload.projectType ?? "",
          message: payload.message ?? "",
        },
      }),
      invalidatesTags: ["Request"],
    }),
    fetchRequests: builder.query<RequestRecord[], void>({
      query: () => ({
        url: "/CreateRequest",
        method: "GET",
      }),
      transformResponse: (response: RequestApiResponse<unknown> | unknown) =>
        getRequestItems(response).map((item) => normalizeRequestRecord(item)),
      providesTags: ["Request"],
    }),
    updateRequest: builder.mutation<RequestApiResponse, UpdateRequestPayload>({
      query: ({ id, ...payload }) => ({
        url: `/CreateRequest/${id}`,
        method: "PUT",
        body: {
          id,
          name: payload.name ?? "",
          email: payload.email ?? "",
          phone: normalizePhoneNumber(payload.phone),
          city: payload.city ?? "",
          projectType: payload.projectType ?? "",
          message: payload.message ?? "",
          status: payload.status ?? "pending",
        },
      }),
      invalidatesTags: ["Request"],
    }),
    deleteRequest: builder.mutation<RequestApiResponse, string>({
      query: (id) => ({
        url: `/CreateRequest/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Request"],
    }),
  }),
});

export const {
  useCreateRequestMutation,
  useDeleteRequestMutation,
  useFetchRequestsQuery,
  useUpdateRequestMutation,
} = requestsApi;
